import theme from "@theme";
import { Button, Heading, Icon, Svg } from "@thetreep/ui";
import { useTranslation } from "react-i18next";
import Logo from "@/assets/assurance_maladie.svg";
import ServiceLogo from "@/assets/service_trajet_sante.svg";
import LogoTheTreep from "@/assets/logo_thetreep.svg";
import {
  forwardRef,
  ReactNode,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { DAP } from "@gen/dap/v1/dap_pb";
import { formatCoordinates } from "@utils/coordinates";
import { Place, ScoredPlace, ScoredPlaceSchema } from "@gen/place/v1/place_pb";
import { near } from "@services/place";
import { create } from "@bufbuild/protobuf";
import { Control, Controller, useController, useWatch } from "react-hook-form";
import { CalendarPanel } from "./CalendarPanel";
import { NativeDateTimeInput } from "./NativeDateTimeInput";
import { SearchNearPanel } from "./SearchNearPanel";
import i18n from "@libs/i18n";
import { FieldError } from "@components/form/FieldError";
import FloatingTooltip from "@components/common/FloatingTooltip";
import { DATE_TYPE_OPTIONS, SearchRequest } from "@services/travel";

interface TravelFormProps {
  dap: DAP;
  control: Control<SearchRequest>;
}

const TravelForm = forwardRef(function TravelForm(
  { dap, control }: TravelFormProps,
  ref,
) {
  const { t } = useTranslation();
  const { outwardDate, inwardDate } = useWatch({ control });
  const { field: originField, fieldState: originState } = useController({
    control,
    name: "origin",
    rules: { required: t("form.origin.required") },
  });

  const [nearPlaces, setNearPlaces] = useState<ScoredPlace[]>();
  useEffect(() => {
    (async () => {
      try {
        setNearPlaces(await near(dap));
      } catch (error) {
        console.error("Error fetching nearby places:", error);
        setNearPlaces([]);
      }
    })();
  }, [dap]);

  const originOptions = useMemo<ScoredPlace[]>(() => {
    const pois = nearPlaces ?? [];
    return dap.origin
      ? [create(ScoredPlaceSchema, { place: dap.origin }), ...pois]
      : pois;
  }, [nearPlaces, dap.origin]);

  useEffect(() => {
    if (!nearPlaces || originField.value) {
      return;
    }
    if (nearPlaces.length === 1 && nearPlaces[0].place) {
      originField.onChange(nearPlaces[0].place);
    } else if (nearPlaces.length === 0 && dap.origin) {
      originField.onChange(dap.origin);
    }
  }, [nearPlaces]);
  const [selectedOption, setSelectedOption] = useState<
    "outwardDate" | "inwardDate" | "origin" | null
  >(null);
  useEffect(() => {
    if (!selectedOption || !window.matchMedia("(max-width: 767px)").matches) {
      return;
    }
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [selectedOption]);
  const additionalPassengers = useWatch({
    control,
    name: "additionalPassengers",
  });
  const outwardDateRef = useRef<HTMLDivElement>(null);
  useImperativeHandle(ref, () => ({
    scrollToButton: () => {
      outwardDateRef.current?.scrollIntoView({ behavior: "smooth" });
    },
  }));

  function displayPlace(p: Place | undefined): string {
    if (!p) {
      return "";
    }
    return p.name || formatCoordinates(p.latitude || 0, p.longitude || 0);
  }

  function displayDate(d: Date | undefined): string {
    if (!d) {
      return `+ ${t("add")}`;
    }
    const options: Intl.DateTimeFormatOptions = {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "numeric",
      minute: "numeric",
    };
    return new Intl.DateTimeFormat(i18n.language, options).format(d);
  }

  function dateField(
    optionKey: "outwardDate" | "inwardDate",
    prefix: string,
    requiredMsg: string,
    date: Date | undefined,
    testId: string,
  ) {
    return (
      <div>
        <Controller
          name={`${optionKey}.date`}
          control={control}
          rules={{
            required: requiredMsg,
            // revalide le retour quand l'aller change
            deps: optionKey === "outwardDate" ? ["inwardDate.date"] : undefined,
            validate:
              optionKey === "inwardDate"
                ? (d: Date | undefined) =>
                    !d ||
                    !outwardDate?.date ||
                    d.getTime() - outwardDate.date.getTime() >= 3600_000 ||
                    t("form.returnDate.minGap")
                : undefined,
          }}
          render={({ field, fieldState }) => (
            <div className="relative">
              <FormButton
                data-testid={testId}
                icon="calendar_2_line"
                prefix={prefix}
                suffix={displayDate(date)}
                className={
                  selectedOption === optionKey
                    ? "border-grey-blue-500 border-2"
                    : ""
                }
                error={fieldState.error?.message}
                onClick={() => setSelectedOption(optionKey)}
              />
              <NativeDateTimeInput
                data-testid={`${testId}-native`}
                value={field.value}
                min={
                  optionKey === "inwardDate" && outwardDate?.date
                    ? outwardDate.date
                    : new Date()
                }
                onChange={field.onChange}
              />
            </div>
          )}
        />
        <Controller
          name={`${optionKey}.type`}
          control={control}
          render={({ field }) => (
            <div className="md:hidden mt-2 flex rounded-md border border-grey-blue-300 p-1 bg-white">
              {DATE_TYPE_OPTIONS.map(({ labelKey, value }) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed={(field.value ?? "departure") === value}
                  className={`flex-1 rounded py-2 text-sm ${
                    (field.value ?? "departure") === value
                      ? "bg-grey-blue-500 text-white"
                      : "text-blue-medium"
                  }`}
                  onClick={() => field.onChange(value)}
                >
                  {t(labelKey)}
                </button>
              ))}
            </div>
          )}
        />
      </div>
    );
  }

  function renderOptionPanel() {
    switch (selectedOption) {
      case "outwardDate":
      case "inwardDate":
        return (
          <CalendarPanel
            control={control}
            name={selectedOption}
            minDate={new Date()}
            onSelectedNameChange={setSelectedOption}
          />
        );
      case "origin":
        return (
          <SearchNearPanel
            places={nearPlaces ? originOptions : undefined}
            onChange={(e) => {
              originField.onChange(e);
              setSelectedOption(null);
            }}
          />
        );
      default:
        return <PlaceholderPanel />;
    }
  }

  const trajetInfoMessages: string[] = [
    t("search.travelFormInfos.incorrectInfo"),
    t("search.travelFormInfos.moreLuggages"),
    t("search.travelFormInfos.connections"),
  ];

  return (
    <div
      className="
            w-full
            bg-neutral-50 p-8 rounded-md
            flex flex-col gap-2"
    >
      <Heading>{t("travel")}</Heading>
      <div ref={outwardDateRef} className="flex flex-col gap-4 mt-2">
        {trajetInfoMessages.map((message) => (
          <div
            className="flex items-start gap-2 text-grey-blue-500 text-xs"
            ref={outwardDateRef}
          >
            <Icon
              src="information_line"
              size={16}
              color={theme.colors["grey-blue"][500]}
            />
            <span>{message}</span>
          </div>
        ))}
      </div>

      <div className="border-t border-light-blue-200 my-6"></div>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_420px] gap-6 h-fit">
        <div className="flex flex-col gap-2">
          <span className="font-jakarta text-xl text-neutral-900 my-2">
            {t("search.originDestination")}
          </span>
          <FormButton
            icon="location_line"
            prefix={`${t("from")} ${
              originField.value ? displayPlace(originField.value) : "..."
            }`}
            suffix={
              originField.value ? undefined : `+ ${t("search.selectLocation")}`
            }
            error={originState.error?.message}
            className={
              selectedOption === "origin" ? "border-grey-blue-500 border-2" : ""
            }
            onClick={() => setSelectedOption("origin")}
          />
          <FormButton
            icon="location_line"
            prefix={`${t("to")} ${
              dap?.destination?.name ||
              formatCoordinates(
                dap?.destination?.latitude || 0,
                dap?.destination?.longitude || 0,
              )
            }`}
            tooltip={t("search.destinationTooltip")}
            disabled
          />
          <span className="font-jakarta text-xl text-neutral-900 my-2">
            {t("search.departureReturn")}
          </span>
          {dateField(
            "outwardDate",
            t("search.departureAt"),
            t("form.departureDate.required"),
            outwardDate?.date,
            "search-departuredate-button",
          )}
          {dateField(
            "inwardDate",
            t("search.returnAt"),
            t("form.returnDate.required"),
            inwardDate?.date,
            "search-returndate-button",
          )}
          <div className="flex items-center justify-between">
            <span className="font-jakarta text-xl text-neutral-900 my-2">
              {t("search.passengers")}
            </span>
            <div className="flex items-center gap-2">
              <Icon
                src="group_line"
                size={16}
                color={theme.colors["blue-medium"].DEFAULT}
              />
              <span className="text-blue-medium text-sm">
                {(additionalPassengers?.length || 0) + 1}
              </span>
            </div>
          </div>
        </div>

        <div
          className={`flex items-center justify-center md:h-[449px] ${
            selectedOption
              ? "fixed inset-0 z-[1100] flex-col justify-start overflow-auto bg-white md:static md:z-auto md:flex-row md:justify-center md:overflow-visible md:bg-transparent"
              : ""
          }`}
        >
          {selectedOption && (
            <div className="sticky top-0 z-10 flex w-full items-center justify-between border-b border-light-blue-200 bg-white py-2 pl-4 pr-2 md:hidden">
              <span className="font-jakarta text-lg text-neutral-900">
                {selectedOption === "origin"
                  ? t("search.selectLocation")
                  : t("search.departureReturn")}
              </span>
              <button
                type="button"
                aria-label={t("modal.close")}
                className="p-2"
                onClick={() => setSelectedOption(null)}
              >
                <Icon src="close_line" size={24} />
              </button>
            </div>
          )}
          <div
            className={
              selectedOption
                ? "flex w-full flex-col gap-4 p-4 md:contents"
                : "contents"
            }
          >
            {renderOptionPanel()}
            {(selectedOption === "outwardDate" ||
              selectedOption === "inwardDate") && (
              <div className="w-full md:hidden">
                <Button
                  type="button"
                  width="100%"
                  onClick={() => setSelectedOption(null)}
                >
                  {t("validate")}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

export default TravelForm;

interface FormButtonProps {
  disabled?: boolean;
  icon: string;
  prefix: string;
  suffix?: string;
  error?: string;
  className?: string;
  tooltip?: ReactNode;
  "data-testid"?: string;
  onClick?: () => void;
}

export function FormButton({
  icon,
  prefix,
  suffix,
  disabled,
  className,
  error,
  tooltip,
  "data-testid": testId,
  onClick,
}: FormButtonProps) {
  const content = (
    <div className="w-full">
      <button
        data-testid={testId}
        disabled={disabled}
        className={`flex items-center justify-between gap-2 w-full
                text-blue-medium text-xs
                disabled:text-zinc-600 disabled:cursor-not-allowed disabled:bg-neutral-100
                border border-grey-blue-300 rounded-md p-4
                hover:border-blue-medium hover:bg-neutral-100
                active:border-blue-medium active:bg-neutral-200 ${className}`}
        onClick={onClick}
      >
        <div className="flex items-center gap-2">
          <Icon
            src={icon}
            size={16}
            color={
              disabled
                ? theme.colors.zinc[600]
                : theme.colors["blue-medium"].DEFAULT
            }
          />
          <span className="font-bold">{prefix}</span>
        </div>
        {!suffix ? <></> : <span>{suffix}</span>}
      </button>
      <FieldError error={error} />
    </div>
  );

  return tooltip ? (
    <FloatingTooltip content={tooltip}>{content}</FloatingTooltip>
  ) : (
    content
  );
}

function PlaceholderPanel() {
  const { t } = useTranslation();

  return (
    <div
      className="flex flex-col items-center justify-between gap-8 p-12
                    h-full w-full rounded-md bg-cnam-light-100"
    >
      <div className="flex items-center justify-between h-12">
        <img
          src={Logo}
          alt="L'assurance Maladie"
          className="h-12 max-w-[50%]"
        />
        <div className="h-full border-r border-cnam-blue"></div>
        <img
          src={ServiceLogo}
          alt="Service Trajet Santé"
          className="h-12 max-w-[40%]"
        />
      </div>
      <span className="font-jakarta text-xl text-zinc-600 text-center">
        {t("search.panelPlaceholder")}
      </span>
      <div className="h-4 justify-start items-center gap-[5px] flex">
        <div className="text-primary-strong text-sm leading-none">
          {t("header.poweredBy")}
        </div>
        <Svg
          src={LogoTheTreep}
          color={theme.colors.primary.strong}
          width="85px"
        />
      </div>
    </div>
  );
}
